<template>
  <div class="cmaker-runs-page">
    <header class="page-hero">
      <div>
        <h4 class="page-hero__title mb-1">Расписание</h4>
        <p class="page-hero__subtitle mb-0">История запусков пайплайна</p>
      </div>
      <div class="d-flex gap-2">
        <router-link to="/streams" class="btn btn-light btn-sm page-hero__back">
          <i class="bi bi-arrow-left me-1"></i>К стримам
        </router-link>
      </div>
    </header>

    <div class="filter-bar">
      <div class="filter-group">
        <label class="filter-label">Стрим</label>
        <select class="form-select form-select-sm" v-model="filters.streamId" @change="reload">
          <option :value="''">Все</option>
          <option v-for="s in streams" :key="s.id" :value="s.id">
            {{ s.name || '#' + s.id }}
          </option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">Статус</label>
        <select class="form-select form-select-sm" v-model="filters.status" @change="reload">
          <option :value="''">Все</option>
          <option v-for="s in statuses" :key="s" :value="s">{{ s }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">С</label>
        <input type="datetime-local" class="form-control form-control-sm" v-model="filters.from" @change="reload" />
      </div>
      <div class="filter-group">
        <label class="filter-label">По</label>
        <input type="datetime-local" class="form-control form-control-sm" v-model="filters.to" @change="reload" />
      </div>
      <div class="filter-group filter-group--actions">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="resetFilters">Сбросить</button>
      </div>
    </div>

    <div v-if="loading" class="card-load-state">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
    </div>

    <div v-else-if="loadError" class="alert alert-danger">{{ loadError }}</div>

    <div v-else class="table-wrap">
      <table class="table table-sm runs-table">
        <thead>
          <tr>
            <th>Когда</th>
            <th>Стрим</th>
            <th>Триггер</th>
            <th>Режим</th>
            <th>Статус</th>
            <th>Публикация</th>
            <th>Длительность</th>
            <th>Стоимость</th>
            <th>Контент</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.jobId">
            <td>{{ formatDate(r.createdAt) }}</td>
            <td>
              <router-link v-if="r.streamId" :to="`/streams/${r.streamId}/schedule`">
                {{ r.streamName || '#' + r.streamId }}
              </router-link>
            </td>
            <td>{{ r.trigger || '—' }}</td>
            <td>{{ r.mode || '—' }}</td>
            <td>
              <span :class="['status-badge', `status-badge--${(r.jobStatus || '').toLowerCase()}`]">
                {{ r.jobStatus || '—' }}
              </span>
            </td>
            <td>
              <span :class="publishInfo(r).cls" :title="publishInfo(r).title">{{ publishInfo(r).label }}</span>
            </td>
            <td class="text-muted small">{{ formatDuration(r.startedAt, r.finishedAt) }}</td>
            <td>
              <span v-if="r.costTotal != null">
                {{ formatCost(r.costTotal) }} <span class="text-muted small">{{ r.costCurrency || '' }}</span>
              </span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              <router-link v-if="r.contentId && r.streamId" :to="`/streams/${r.streamId}/content/${r.contentId}/edit`">
                #{{ r.contentId }}
              </router-link>
              <span v-else class="text-muted">—</span>
            </td>
            <td>
              <div class="d-flex gap-1 justify-content-end">
                <router-link v-if="r.jobId" :to="`/cmaker/runs/${r.jobId}`" class="btn btn-outline-primary btn-sm">
                  Открыть
                </router-link>
                <span v-else class="text-muted">—</span>
                <button
                  v-if="isTerminal(r.jobStatus)"
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  :disabled="deletingId === r.jobId"
                  title="Удалить run"
                  @click="onDelete(r)"
                >
                  <span v-if="deletingId === r.jobId" class="spinner-border spinner-border-sm"></span>
                  <i v-else class="bi bi-trash"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="10" class="text-muted text-center py-4">Нет запусков по выбранным фильтрам.</td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-bar">
        <button class="btn btn-outline-secondary btn-sm" :disabled="page <= 0" @click="changePage(page - 1)">Назад</button>
        <span class="text-muted small">
          Страница {{ page + 1 }} из {{ Math.max(totalPages, 1) }} · всего {{ totalElements }}
        </span>
        <button class="btn btn-outline-secondary btn-sm" :disabled="page + 1 >= totalPages" @click="changePage(page + 1)">Вперёд</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import { listRuns, deleteRun } from '@/services/cmakerRunService'

const route = useRoute()

const loading = ref(false)
const loadError = ref('')
const rows = ref([])
const deletingId = ref(null)

const TERMINAL_STATUSES = ['SUCCEEDED', 'FAILED', 'CANCELLED', 'SKIPPED']
function isTerminal(status) {
  return TERMINAL_STATUSES.includes(status)
}

async function onDelete(r) {
  if (!confirm('Удалить run и все его артефакты? Действие необратимо.')) return
  deletingId.value = r.jobId
  try {
    await deleteRun(r.jobId)
    await reload()
  } catch (e) {
    console.error(e)
    alert(e?.response?.data?.message || 'Не удалось удалить run.')
  } finally {
    deletingId.value = null
  }
}
const page = ref(0)
const size = ref(20)
const totalPages = ref(0)
const totalElements = ref(0)
const streams = ref([])

const statuses = ['CREATED', 'SUBMITTING', 'SUBMITTED', 'RUNNING', 'SUCCEEDED', 'SKIPPED', 'FAILED', 'CANCELLED']

const filters = reactive({
  streamId: '',
  status: '',
  from: '',
  to: '',
})

function formatDate(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

function formatDuration(start, end) {
  if (!start) return '—'
  const finish = end ? new Date(end) : new Date()
  const startD = new Date(start)
  if (Number.isNaN(startD.getTime())) return '—'
  const ms = Math.max(0, finish - startD)
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}s`
  const min = Math.floor(sec / 60)
  const rest = sec % 60
  if (min < 60) return `${min}m ${rest}s`
  const h = Math.floor(min / 60)
  return `${h}h ${min % 60}m`
}

function formatCost(v) {
  if (v == null) return '—'
  const n = Number(v)
  return Number.isFinite(n) ? n.toFixed(4) : String(v)
}

// astra-side publication outcome for the "Публикация" column. publishError (set when
// WordPress auto-publish or article materialization fails — e.g. the missing-image case)
// always wins so failures are visible at a glance; otherwise we map the content status.
function publishInfo(r) {
  if (r.publishError) {
    return { label: 'Ошибка', cls: 'text-danger fw-semibold', title: r.publishError }
  }
  switch (r.contentStatus) {
    case 'PUBLISHED': return { label: 'Опубликовано', cls: 'text-success', title: '' }
    case 'ERROR':     return { label: 'Ошибка', cls: 'text-danger fw-semibold', title: 'Ошибка публикации' }
    case 'READY':     return { label: 'Готов, не опубл.', cls: 'text-muted small', title: 'Материал готов, но не опубликован' }
    case 'ARCHIVE':   return { label: 'В архиве', cls: 'text-muted small', title: '' }
    default: break
  }
  if (r.mode === 'GENERATE_ONLY') {
    return { label: 'Без публикации', cls: 'text-muted small', title: 'Режим только генерации' }
  }
  return { label: '—', cls: 'text-muted', title: '' }
}

function toIsoOrNull(local) {
  if (!local) return null
  try { return new Date(local).toISOString() } catch { return null }
}

async function loadStreamsList() {
  try {
    const { data } = await axios.get('/api/streams', { params: { page: 0, size: 200 } })
    const content = Array.isArray(data?.content) ? data.content : (Array.isArray(data) ? data : [])
    streams.value = content
  } catch (e) {
    console.error('Не удалось загрузить стримы', e)
  }
}

async function reload() {
  loading.value = true
  loadError.value = ''
  try {
    const resp = await listRuns({
      page: page.value,
      size: size.value,
      streamId: filters.streamId || undefined,
      status: filters.status || undefined,
      from: toIsoOrNull(filters.from),
      to: toIsoOrNull(filters.to),
    })
    rows.value = Array.isArray(resp?.content) ? resp.content : []
    // Spring Boot's VIA_DTO page serialization nests paging metadata under `page`
    // ({ content, page: { totalElements, totalPages, ... } }); fall back to the
    // legacy top-level fields for safety.
    const meta = resp?.page ?? resp
    totalPages.value = meta?.totalPages ?? 0
    totalElements.value = meta?.totalElements ?? rows.value.length
  } catch (e) {
    console.error(e)
    loadError.value = 'Не удалось загрузить список run-ов.'
  } finally {
    loading.value = false
  }
}

function changePage(next) {
  if (next < 0) return
  page.value = next
  reload()
}

function resetFilters() {
  filters.streamId = ''
  filters.status = ''
  filters.from = ''
  filters.to = ''
  page.value = 0
  reload()
}

onMounted(async () => {
  // Pre-select the stream filter when arriving from a stream's schedule page
  // (?streamId=...). Numeric to match the select's numeric option values.
  const streamIdParam = Number(route.query.streamId)
  if (Number.isFinite(streamIdParam) && streamIdParam > 0) {
    filters.streamId = streamIdParam
  }
  await loadStreamsList()
  await reload()
})
</script>

<style scoped>
.cmaker-runs-page {
  display: flex;
  flex-direction: column;
  height: calc(var(--app-vh, 1vh) * 100 - 2px);
  padding: 8px;
  background:
    radial-gradient(circle at 2% 3%, rgba(66, 153, 225, 0.2), transparent 36%),
    radial-gradient(circle at 98% 2%, rgba(56, 189, 248, 0.18), transparent 32%),
    #f3f7ff;
  overflow: hidden;
  box-sizing: border-box;
  gap: 8px;
}

.page-hero {
  flex: 0 0 auto;
  padding: 12px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #10305f 0%, #1b4f93 60%, #1e67b7 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.page-hero__back { color: #103f77; border-color: #cfe2ff; font-weight: 700; }

.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 14px;
  padding: 10px 12px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #dfe7f5;
}

.filter-group { display: flex; flex-direction: column; gap: 2px; min-width: 140px; }
.filter-group--actions { justify-content: flex-end; }
.filter-label { font-size: 11px; color: #4d5f82; text-transform: uppercase; font-weight: 700; }

.table-wrap {
  flex: 1 1 auto;
  overflow: auto;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #dfe7f5;
  padding: 8px 12px;
}

.runs-table { font-size: 13px; }
.runs-table th { color: #4d5f82; text-transform: uppercase; font-size: 11px; }

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: #eef3ff;
  color: #2f4f88;
  border: 1px solid #d8e5fb;
}
.status-badge--succeeded { background: #e8f7ee; color: #1c7a3a; border-color: #b8e0c6; }
.status-badge--failed { background: #fdecec; color: #a4262c; border-color: #f4bcbf; }
.status-badge--cancelled { background: #f3f5f9; color: #4f5b75; border-color: #d6dbe5; }
.status-badge--skipped { background: #eef3ff; color: #2f4f88; border-color: #d8e5fb; }
.status-badge--running, .status-badge--submitted, .status-badge--submitting { background: #fff7e0; color: #8b6608; border-color: #f1dc9c; }

.pagination-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 10px 0 4px;
}

.card-load-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}
</style>
